import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  ArrowLeft,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  Loader2,
  AlertCircle,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const typeIcons = {
  bug: Bug,
  suggestion: Lightbulb,
  other: HelpCircle
};

const typeColors = {
  bug: 'bg-red-500',
  suggestion: 'bg-blue-500',
  other: 'bg-gray-500'
};

export default function AdminFeedback() {
  const navigate = useNavigate();
  const { accessToken, loading: authLoading, user } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchFeedback = useCallback(async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/feedback`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (response.status === 403) {
        setLoadError('Access denied. You must be an admin to view this page.');
        setIsLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch feedback');
      }
      
      const data = await response.json();
      setFeedback(data.feedback || []);
      setTotalCount(data.total_count || 0);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      setLoadError('Failed to load feedback. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!accessToken) {
      navigate('/login');
      return;
    }
    
    fetchFeedback();
  }, [authLoading, accessToken, fetchFeedback, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading feedback...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <Shield className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="font-heading font-bold text-xl mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">{loadError}</p>
          <Link to="/dashboard">
            <Button>Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b-2 border-border sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="h-10 border-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight flex items-center gap-3">
                <Shield className="w-8 h-8 text-primary" />
                Admin: User Feedback
              </h1>
              <p className="text-muted-foreground mt-2">
                {totalCount} total submissions (newest first)
              </p>
            </div>
          </div>

          {feedback.length === 0 ? (
            <div className="border-2 border-dashed border-border p-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="font-heading font-bold text-lg mb-2">No Feedback Yet</h2>
              <p className="text-muted-foreground">
                User feedback submissions will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedback.map((item, index) => {
                const TypeIcon = typeIcons[item.type] || HelpCircle;
                const typeColor = typeColors[item.type] || 'bg-gray-500';
                
                return (
                  <motion.div
                    key={item.id}
                    className="border-2 border-border p-6 hover:border-primary transition-colors"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`feedback-item-${item.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 ${typeColor} flex items-center justify-center flex-shrink-0`}>
                        <TypeIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 text-xs font-bold uppercase ${typeColor} text-white`}>
                            {item.type}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(item.created_at)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            item.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                            item.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                        
                        <p className="text-foreground mb-3 whitespace-pre-wrap">
                          {item.message}
                        </p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {item.name && item.name !== 'Anonymous' && (
                            <span>
                              <strong>Name:</strong> {item.name}
                            </span>
                          )}
                          {item.email && (
                            <span>
                              <strong>Email:</strong> {item.email}
                            </span>
                          )}
                          {item.page_url && (
                            <span className="truncate max-w-xs">
                              <strong>Page:</strong> {item.page_url}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
        </main>
        {/* Footer */}
        <footer className="border-t-2 border-border py-8 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-sm">CALC AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © Powered by FHYAS LLC.
            </p>
          </div>
        </footer>
    </div>
  );
}

